import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Award, Search, Filter, Download, User, Calendar, DollarSign, Plus, X, RefreshCw, Database, AlertTriangle, Edit, Trash2, CalendarDays, Globe, Upload, Lock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useNotification } from '../contexts/NotificationContext';
import { useSubscription } from '../hooks/useSubscription';
import { useGlobalLoading } from '../hooks/useGlobalLoading';
import { getApiUrl } from "../config/api";
import axios from 'axios';

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export default function Entries() {
  const { showNotification } = useNotification();
  const { hasFeature, subscription, features } = useSubscription();
  const { withLoading } = useGlobalLoading();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSourceType, setFilterSourceType] = useState('all');
  const [sortBy, setSortBy] = useState('show_start_date');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Manual entry form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [profileDefaults, setProfileDefaults] = useState({ name: '', email: '' });
  
  // Family member state
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedFamilyMemberForEntry, setSelectedFamilyMemberForEntry] = useState('primary');
  const [newEntry, setNewEntry] = useState({
    confirmationNumber: '',
    dateentered: '',
    source: 'manual',
    family_member_id: 'primary',
    show: {
      showname: '',
      showdate: '',
      showlocation: ''
    },
    events: [
      {
        eventname: '',
        perfname: '',
        EntryAmount: 0,
        carryOvers: [],
        contestants: [
          {
            FirstName: '',
            LastName: '',
            stockName: '',
            positiondesc: ''
          }
        ]
      }
    ],
    fees: [
      {
        description: '',
        quantity: 1,
        amount: 0
      }
    ],
    other: [],
    payments: []
  });

  // SQL Server integration state
  const [showSqlServerForm, setShowSqlServerForm] = useState(false);
  const [sqlServerData, setSqlServerData] = useState({
    associationKey: '',
    memberKey: '',
    memberPass: '',
    confirmationNumber: ''
  });
  const [sqlServerEntries, setSqlServerEntries] = useState([]);
  const [sqlServerLoading, setSqlServerLoading] = useState(false);
  const [hasApprovedEmail, setHasApprovedEmail] = useState(false);

  // Family associations state
  const [familyAssociations, setFamilyAssociations] = useState([]);
  const [selectedFamilyMemberId, setSelectedFamilyMemberId] = useState('');
  const [selectedUserAssociation, setSelectedUserAssociation] = useState(null);
  const [userAssociations, setUserAssociations] = useState([]);

  // Show selection and iframe state
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [availableShows, setAvailableShows] = useState([]);
  const [showsLoading, setShowsLoading] = useState(false);
  const [selectedShow, setSelectedShow] = useState(null);
  const [showIframe, setShowIframe] = useState(false);
  const [associationName, setAssociationName] = useState('');
  
  
  // Iframe session monitoring
  const [iframeUrl, setIframeUrl] = useState('');
  const [iframeNavigationCount, setIframeNavigationCount] = useState(0);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [showSessionNoteDetails, setShowSessionNoteDetails] = useState(false);
  const [showAssociationSelectionModal, setShowAssociationSelectionModal] = useState(false);
  const [primaryAccountSettings, setPrimaryAccountSettings] = useState({ associationKey: '', userId: '', userPassword: '', associationName: '' });
  const [selectedOnlineEntryAssociationId, setSelectedOnlineEntryAssociationId] = useState('primary');

  const onlineAssociations = useMemo(() => {
    
    const associations = [];
    
    // Add all associations from familyAssociations (which now includes primary + family)
    for (const assoc of familyAssociations) {
      if (!assoc.associationKey) {
        continue;
      }
      
      associations.push({
        id: assoc.isPrimary ? 'primary' : `family_${assoc.id}`,
        associationKey: assoc.associationKey || '',
        userId: assoc.userId || '',
        userPassword: assoc.userPassword || '',
        associationName: assoc.associationName || ''
      });
    }
    return associations;
  }, [familyAssociations]);

  // Load settings for pre-filling import form
  useEffect(() => {
    loadSettingsForImport();
    loadProfileDefaults();
    checkApprovalStatus();
  }, []);

  const checkApprovalStatus = async () => {
    try {
      const response = await axios.get(getApiUrl('/api/data-import/check-approval'));
      setHasApprovedEmail(response.data.hasApprovedEmail);
    } catch (error) {
      console.error('Error checking approval status:', error);
    }
  };

  const loadSettingsForImport = async () => {
    try {
      const { data, status } = await axios.get(getApiUrl('/api/user-associations'));
      if (status === 200 && Array.isArray(data)) {
        const mapped = data.map(assoc => ({
          id: assoc.id,                      // unique association ID
          familyMemberId: assoc.family_member_id,  // family member ID (null for current user)
          familyMemberName: assoc.name || '',
          associationKey: assoc.association_key || '',
          associationName: assoc.association_name || '',
          userId: assoc.user_id_at_association || '',
          userPassword: assoc.user_password_at_association || '',
        }));
  
        setFamilyAssociations(mapped);
  
        // Auto-select the FIRST association if present
        const first = mapped[0];
        if (first) {
          // If this view needs to prefill SQL params:
          setSqlServerData({
            associationKey: first.associationKey,
            memberKey: first.userId,
            memberPass: first.userPassword,
          });

          // If you want to auto-run the fetch based on those params, do it here:
          // fetchSqlData(first.associationKey, first.userId, first.userPassword);
        } else {
          // Optional: clear selections when no data
          setSqlServerData({ associationKey: '', memberKey: '', memberPass: '' });
        }
      }
    } catch (error) {
      console.error('Error loading associations for import:', error);
      // Optional: set an error state here
    }
  };
  
  useEffect(() => {
    if (familyAssociations.length > 0) {
      const first = familyAssociations[0];
      setSelectedFamilyMemberId(String(first.id));
      setSqlServerData({
        associationKey: first.associationKey || '',
        memberKey: first.userId || '',
        memberPass: first.userPassword || '',
      });
    } else {
      setSelectedFamilyMemberId('');
      setSqlServerData({ associationKey: '', memberKey: '', memberPass: '' });
    }
  }, [familyAssociations]);

  const handleFamilyMemberChange = (id) => {
    setSelectedFamilyMemberId(id);
    const a = familyAssociations.find(x => String(x.id) === String(id)); // robust to number/string
    if (a) {
      setSqlServerData({
        associationKey: a.associationKey || '',
        memberKey: a.userId || '',
        memberPass: a.userPassword || '',
      });
    }
  };
 
  const loadProfileDefaults = async () => {
    try {
      const response = await axios.get(getApiUrl('/api/profile'));
      if (response.status === 200 && response.data) {
        const { name = '', email = '' } = response.data;
        setProfileDefaults({ name, email });
      }
    } catch (error) {
      console.error('Error loading profile defaults:', error);
    }
  };

  const loadFamilyMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const [userRes, familyRes] = await Promise.all([
        axios.get(getApiUrl('/api/profile'), { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(getApiUrl('/api/settings/family-members'), { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const currentUser = userRes.data;
      const familyMembers = familyRes.data || [];
            
      // Create family members list with current user first
      const members = [
        { id: 'primary', name: currentUser.name || 'Primary User', isCurrentUser: true },
        ...familyMembers.map(member => ({
          id: member.id,
          name: member.name,
          isCurrentUser: false
        }))
      ];
      
      setFamilyMembers(members);
      setSelectedFamilyMemberForEntry('primary'); // Default to current user
    } catch (error) {
      console.error('Error loading family members:', error);
    }
  };

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState(null);

  // Entry details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDisclaimerDetails, setShowDisclaimerDetails] = useState(false);

  useEffect(() => {
    fetchEntries();
  }, []);

  // Load family members when subscription data is available
  useEffect(() => {
    if (hasFeature('family_plan')) {
      loadFamilyMembers();
    }
  }, [subscription, features, hasFeature]);

  // Calculate total fees - this is now handled in the render where we display the total

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const response = await withLoading(
        () => axios.get(getApiUrl('/api/entries')),
        'Loading entries...'
      );

      if (response.status === 200) {
        const data = response.data;
        setEntries(data);
        

      } else {
        throw new Error('Failed to fetch entries');
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      showNotification('Failed to load entries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const addNewEntry = async () => {
    try {
      setLoading(true);
      // Wrap the data in entry_data field as expected by the API
      const entryData = {
        entry_data: { ...newEntry }
      };

      // If not on family plan, ensure family_member_id is not sent
      try {
        if (!hasFeature('family_plan')) {
          entryData.entry_data.family_member_id = 'primary';
        }
      } catch (_) {
        entryData.entry_data.family_member_id = 'primary';
      }
      const response = await axios.post(getApiUrl('/api/entries'), entryData);

      // Treat any 2xx as success (API returns 201 for created)
      if (response && response.status >= 200 && response.status < 300) {
        showNotification('Entry added successfully', 'success');
        setShowAddForm(false);
        resetForm();
        fetchEntries(); // Refresh the list
      }
    } catch (error) {
      console.error('Error adding entry:', error);
      showNotification('Failed to add entry', 'error');
    } finally {
      setLoading(false);
    }
  };

  const editEntry = (entry) => {
    setEditingEntry(entry);

    const entryData = {
      confirmationNumber: entry.entry_data?.confirmationNumber || '',
      dateentered: entry.entry_data?.dateentered || '',
      source: entry.entry_data?.source || 'manual',
      family_member_id: entry.family_member_id || 'primary',
      show: {
        showname: entry.entry_data?.show?.showname || '',
        showdate: entry.entry_data?.show?.showdate || '',
        showlocation: entry.entry_data?.show?.showlocation || ''
      },
      events: entry.entry_data?.events?.map(event => ({
        eventname: event.eventname || '',
        perfname: event.perfname || '',
        EntryAmount: parseFloat(event.EntryAmount || 0),
        carryOvers: event.carryOvers || [],
        contestants: event.contestants?.map(contestant => ({
          FirstName: contestant.FirstName || '',
          LastName: contestant.LastName || '',
          stockName: contestant.stockName || '',
          positiondesc: contestant.positiondesc || ''
        })) || []
      })) || [{
        eventname: '',
        perfname: '',
        EntryAmount: 0,
        carryOvers: [],
        contestants: [{
          FirstName: '',
          LastName: '',
          stockName: '',
          positiondesc: ''
        }]
      }],
      fees: entry.entry_data?.fees?.map(fee => ({
        description: fee.description || '',
        quantity: parseInt(fee.quantity || 1),
        amount: parseFloat(fee.amount || 0)
      })) || [{
        description: '',
        quantity: 1,
        amount: 0
      }],
      other: entry.entry_data?.other || [],
      payments: entry.entry_data?.payments || []
    };
    
    setNewEntry(entryData);
    setShowEditForm(true);
  };

  const saveEditedEntry = async () => {
    try {
      setLoading(true);
      // Wrap the data in entry_data field as expected by the API
      const entryData = {
        entry_data: newEntry
      };
      

      
      const response = await axios.put(getApiUrl(`/api/entries/${editingEntry.id}`), entryData);

      if (response.status === 200) {
        showNotification('Entry updated successfully', 'success');
        setShowEditForm(false);
        setEditingEntry(null);
        resetForm();
        fetchEntries();
      } else {
        throw new Error('Failed to update entry');
      }
    } catch (error) {
      console.error('Error updating entry:', error);
      showNotification('Failed to update entry', 'error');
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setShowEditForm(false);
    setEditingEntry(null);
    resetForm();
  };

  const deleteEntry = async (entryId) => {
    try {
      setLoading(true);
      const response = await axios.delete(getApiUrl(`/api/entries/${entryId}`));

      if (response.status === 200) {
        showNotification('Entry deleted successfully', 'success');
        fetchEntries();
      } else {
        throw new Error('Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      showNotification('Failed to delete entry', 'error');
    } finally {
      setLoading(false);
    }
  };



  const confirmDelete = (entry) => {
    setDeletingEntry(entry);
    setShowDeleteConfirm(true);
  };

  const viewEntryDetails = (entry) => {
    setSelectedEntry(entry);
    setShowDetailsModal(true);
  };

  const fetchSqlServerEntries = async () => {
    try {
      // Ensure a show is selected before fetching entries
      if (!selectedKCIRShow) {
        showNotification('Please select a show before fetching entries', 'warning');
        return;
      }

      setSqlServerLoading(true);
      // Fetch all entries for the selected show
      const params = new URLSearchParams({
        associationKey: sqlServerData.associationKey,
        memberKey: sqlServerData.memberKey,
        memberPass: sqlServerData.memberPass,
        showId: selectedKCIRShow.showId
      });

      const response = await axios.get(`${getApiUrl('/api/sqlserver-entries/fetch')}?${params}`);

      if (response.status === 200) {
        const data = response.data;
        setSqlServerEntries(data.entries || []);
        showNotification(`Found ${data.count} entries for ${selectedKCIRShow.showName}`, 'success');
      } else {
        const errorData = response.data;
        throw new Error(errorData.error || 'Failed to fetch entries');
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      showNotification(error.message, 'error');
    } finally {
      setSqlServerLoading(false);
    }
  };

  const deleteSqlServerShow = (showIndex) => {
    setSqlServerEntries(prev => prev.filter((_, index) => index !== showIndex));
  };

  const createCalendarEvent = async (entry) => {
    try {
      const eventData = {
        name: entry.show?.showname || 'Rodeo Event',
        event_date: entry.show?.showdate ? entry.show.showdate.split('T')[0] : new Date().toISOString().split('T')[0],
        start_time: '09:00', // Default start time
        duration_minutes: 480, // 8 hours default
        description: `Rodeo event with ${entry.events?.length || 0} events. Confirmation: ${entry.confirmationNumber}`,
        location: entry.show?.showlocation || 'Rodeo Arena',
        event_type: 'rodeo',
        is_all_day: false
      };

      const response = await axios.post(getApiUrl('/api/calendar/events'), eventData);

      if (response.status !== 200) {
        console.warn(`Failed to create calendar event for ${entry.show?.showname || 'Unknown Show'}`);
      }
    } catch (error) {
      console.error('Error creating calendar event:', error);
    }
  };

  const createCalendarEventFromEntry = async (entry) => {
    try {
      const eventData = {
        name: entry.show_name || 'Rodeo Event',
        event_date: entry.show_start_date ? entry.show_start_date.split('T')[0] : new Date().toISOString().split('T')[0],
        start_time: '09:00', // Default start time
        duration_minutes: 480, // 8 hours default
        description: `Rodeo event${entry.contestant_name ? ` - Contestant: ${entry.contestant_name}` : ''}`,
        location: entry.show_location || 'Rodeo Arena',
        event_type: 'rodeo',
        is_all_day: false
      };

      const response = await axios.post(getApiUrl('/api/calendar/events'), eventData);

      if (response.status === 201) {
        showNotification('Calendar event created successfully', 'success');
      } else {
        throw new Error('Failed to create calendar event');
      }
    } catch (error) {
      console.error('Error creating calendar event:', error);
      showNotification('Failed to create calendar event', 'error');
    }
  };

  // Transform SQL Server entries to new JSON format
  const transformSqlServerEntries = (sqlServerData) => {
    // The stored procedure now returns data in the correct format
    // We just need to ensure the source is set correctly
    return sqlServerData.map(entry => ({
      ...entry,
      source: 'kcirodeo'
    }));
  };

  const saveSqlServerEntriesToDatabase = async (entriesToSave = null) => {
    try {
      setLoading(true);
      const entriesToProcess = entriesToSave || sqlServerEntries;
      
      if (entriesToProcess.length === 0) {
        showNotification('No entries to save', 'warning');
        return;
      }

      // Transform entries to new JSON format and use new endpoint
      const transformedEntries = transformSqlServerEntries(entriesToProcess);
      
      // Get the family member ID from the selected association
      const selectedAssociation = familyAssociations.find(a => String(a.id) === String(selectedFamilyMemberId));
      const familyMemberId = hasFeature('family_plan') && selectedAssociation ? selectedAssociation.familyMemberId : null;
      
      const response = await axios.post(getApiUrl('/api/entries/import'), {
        entries: transformedEntries,
        familyMemberId: familyMemberId
      });
      const result = response.data;

      if (response.status === 200) {
        // Show detailed success message
        let message = result.message;
        if (result.summary) {
          const { total, imported, errors, calendarEventsCreated } = result.summary;
          if (imported > 0 && errors > 0) {
            message = `Successfully processed ${total} entries (${imported} imported, ${errors} errors)`;
          } else if (imported > 0) {
            message = `Successfully imported ${imported} entries`;
          } else if (errors > 0) {
            message = `Failed to import entries: ${errors} errors`;
          }
          
          // Add calendar events info
          if (calendarEventsCreated > 0) {
            message += ` and created ${calendarEventsCreated} calendar events`;
          }
        }
        
        showNotification(message, 'success');
        setShowSqlServerForm(false);
        setSelectedKCIRShow(null);
        setSqlServerEntries([]);
        setSqlServerData({
          associationKey: '',
          memberKey: '',
          memberPass: ''
        });
        fetchEntries();
      } else {
        const errorData = response.data;
        throw new Error(errorData.error || 'Failed to save entries');
      }
    } catch (error) {
      console.error('Error saving SQL Server entries:', error);
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };



  // Fetch available shows for the association
  const fetchAvailableShows = async () => {
    try {
      setShowsLoading(true);
      const params = new URLSearchParams({
        associationKey: sqlServerData.associationKey || '',
        memberKey: sqlServerData.memberKey || '',
        memberPass: sqlServerData.memberPass || ''
      });
      const response = await axios.get(`${getApiUrl('/api/sqlserver-entries/shows-new-entry')}?${params.toString()}`);

      if (response.status === 200) {
        const data = response.data;
        setAvailableShows(data.shows || data);
        setShowSelectionModal(true);
      } else {
        const errorData = response.data;
        throw new Error(errorData.error || 'Failed to fetch available shows');
      }
    } catch (error) {
      console.error('Error fetching available shows:', error);
      showNotification(error.message, 'error');
    } finally {
      setShowsLoading(false);
    }
  };


  // Open SQL Server import form directly
  const openSqlServerImportForm = () => {
    // Check if user has approved email
    if (!hasApprovedEmail && familyAssociations.length === 0) {
      showNotification('Please complete email confirmation in Settings first', 'warning');
      return;
    }

    // Ensure sqlServerData is set when opening the modal
    if (selectedFamilyMemberId && familyAssociations.length > 0) {
      const selectedAssociation = familyAssociations.find(x => String(x.id) === String(selectedFamilyMemberId));
      if (selectedAssociation) {
        setSqlServerData({
          associationKey: selectedAssociation.associationKey || '',
          memberKey: selectedAssociation.userId || '',
          memberPass: selectedAssociation.userPassword || '',
        });
      }
    }
    setShowSqlServerForm(true);
  };


  // Handle show selection and open iframe
  const handleShowSelection = (show) => {
    setSelectedShow(show);
    setShowSelectionModal(false);
    setShowIframe(true);
    // Initialize iframe URL using the association KEY from the selected family member
    const initialUrl = `https://entry.kcirodeo.net/entry/std/Entryform.aspx?Association=${encodeURIComponent(sqlServerData.associationKey)}&Show=${encodeURIComponent(show.showKey)}`;
    
    setIframeUrl(initialUrl);
    setIframeNavigationCount(0);
    setShowSessionWarning(false);
    
    // Store the current association data AND show data for the iframe service to use
    // This ensures the iframe service uses the same association and show that was selected
    const currentAssociationData = {
      associationKey: sqlServerData.associationKey,
      memberKey: sqlServerData.memberKey,
      memberPass: sqlServerData.memberPass,
      showId: show.showId  // Add showId for filtering in the API
    };

    // Store in sessionStorage so the iframe service can access it
    sessionStorage.setItem('currentIframeAssociation', JSON.stringify(currentAssociationData));
  };

  // Close iframe
  const closeIframe = () => {
    setShowIframe(false);
    setSelectedShow(null);
    setIframeUrl('');
    setIframeNavigationCount(0);
    setShowSessionWarning(false);
  };

  // Monitor iframe navigation and session state
  const handleIframeLoad = () => {
    setIframeNavigationCount(prev => prev + 1);
    
    // Show warning after multiple navigations (potential session loss)
    if (iframeNavigationCount >= 8) {
      setShowSessionWarning(true);
    }
  };

  const handleIframeError = (error) => {
    console.error('Iframe error:', error);
    setShowSessionWarning(true);
  };

  const refreshIframe = () => {
    setIframeNavigationCount(0);
    setShowSessionWarning(false);
    // Force iframe refresh by updating the URL
    const currentUrl = `https://entry.kcirodeo.net/entry/std/Entryform.aspx?Association=${encodeURIComponent(sqlServerData.associationKey)}&Show=${encodeURIComponent(selectedShow.showKey)}`;
    setIframeUrl(currentUrl + '&t=' + Date.now());
  };

  const openInNewTab = () => {
    const url = `https://entry.kcirodeo.net/entry/std/Entryform.aspx?Association=${encodeURIComponent(sqlServerData.associationKey)}&Show=${encodeURIComponent(selectedShow.showKey)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const resetForm = () => {
    setNewEntry({
      confirmationNumber: '',
      dateentered: '',
      source: 'manual',
      family_member_id: selectedFamilyMemberForEntry || 'primary',
      show: {
        showname: '',
        showdate: '',
        showlocation: ''
      },
      events: [
        {
          eventname: '',
          perfname: '',
          EntryAmount: 0,
          carryOvers: [],
          contestants: [
            {
              FirstName: profileDefaults.name?.split(' ')[0] || '',
              LastName: profileDefaults.name?.split(' ').slice(1).join(' ') || '',
              stockName: '',
              positiondesc: ''
            }
          ]
        }
      ],
      fees: [
        {
          description: '',
          quantity: 1,
          amount: 0
        }
      ],
      other: [],
      payments: []
    });
  };

  const resetKCIRodeoImport = () => {
    setSelectedKCIRShow(null);
    setKcirImportShows([]);
    setShowKCIRodeoImportModal(false);
    setSqlServerData({
      associationKey: '',
      memberKey: '',
      memberPass: '',
      confirmationNumber: ''
    });
  };

  const resetSqlServerImport = () => {
    setSelectedKCIRShow(null);
    // Preserve loaded association credentials; just clear transient fields
    setSqlServerData(prev => ({
      ...prev,
      confirmationNumber: ''
    }));
    setShowSqlServerForm(false);
    setSqlServerEntries([]);
  };

  const addEvent = () => {
    setNewEntry(prev => ({
      ...prev,
      events: [...prev.events, {
        eventname: '',
        perfname: `Perf ${prev.events.length + 1}`,
        EntryAmount: 0,
        carryOvers: [],
        contestants: [
          {
            FirstName: profileDefaults.name?.split(' ')[0] || '',
            LastName: profileDefaults.name?.split(' ').slice(1).join(' ') || '',
            stockName: '',
            positiondesc: ''
          }
        ]
      }]
    }));
  };

  const removeEvent = (index) => {
    if (newEntry.events.length > 1) {
      setNewEntry(prev => ({
        ...prev,
        events: prev.events.filter((_, i) => i !== index)
      }));
    }
  };

  const updateEvent = (index, field, value) => {
    setNewEntry(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === index ? { ...event, [field]: value } : event
      )
    }));
  };

  const addContestantToEvent = (eventIndex) => {
    setNewEntry(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === eventIndex ? {
          ...event,
          contestants: [...event.contestants, {
            FirstName: profileDefaults.name?.split(' ')[0] || '',
            LastName: profileDefaults.name?.split(' ').slice(1).join(' ') || '',
            stockName: '',
            positiondesc: ''
          }]
        } : event
      )
    }));
  };

  const removeContestantFromEvent = (eventIndex, contestantIndex) => {
    setNewEntry(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === eventIndex ? {
          ...event,
          contestants: event.contestants.filter((_, j) => j !== contestantIndex)
        } : event
      )
    }));
  };

  const updateEventContestant = (eventIndex, contestantIndex, field, value) => {
    setNewEntry(prev => ({
      ...prev,
      events: prev.events.map((event, i) => 
        i === eventIndex ? {
          ...event,
          contestants: event.contestants.map((contestant, j) => 
            j === contestantIndex ? { ...contestant, [field]: value } : contestant
          )
        } : event
      )
    }));
  };

  const addFee = () => {
    setNewEntry(prev => ({
      ...prev,
      fees: [...prev.fees, {
        description: '',
        quantity: 1,
        amount: 0
      }]
    }));
  };

  const removeFee = (index) => {
    if (newEntry.fees.length > 1) {
      setNewEntry(prev => ({
        ...prev,
        fees: prev.fees.filter((_, i) => i !== index)
      }));
    }
  };

  const updateFee = (index, field, value) => {
    setNewEntry(prev => ({
      ...prev,
      fees: prev.fees.map((fee, i) => 
        i === index ? { ...fee, [field]: field === 'amount' ? (parseFloat(value) || 0) : value } : fee
      )
    }));
  };

  const filteredEntries = entries
    .filter(entry => {
      const matchesSearch = 
        entry.show_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.confirmation_number?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSource = filterSourceType === 'all' || entry.entry_data?.source === filterSourceType;
      
      return matchesSearch && matchesSource;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'created_at':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'show_start_date':
          return new Date(b.show_start_date || 0) - new Date(a.show_start_date || 0);


        default:
          return 0;
      }
    });

  const exportEntries = () => {
    const csvContent = [
      ['Show', 'Total Amount', 'Confirmation Number', 'Date Entered', 'Source', 'Date'],
      ...filteredEntries.map(entry => {
        return [
          entry.show_name || 'N/A',
          `$${calculateTotalAmount(entry).toFixed(2)}`,
          entry.confirmation_number || 'N/A',
          entry.entry_data?.dateentered ? 
            format(parseISO(entry.entry_data.dateentered), 'MMM dd, yyyy') : 
            'N/A',
          entry.entry_data?.source === 'kcirodeo' ? 'Imported' : 'Manual',
          format(parseISO(entry.created_at), 'MMM dd, yyyy')
        ];
      })
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `entries-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatFees = (entry) => {
    if (!entry.entry_data?.fees) return 'N/A';
    
    const fees = entry.entry_data.fees;
    
    // Handle new array-based fee structure
    if (Array.isArray(fees)) {
      const feeItems = fees
        .filter(fee => fee.description && fee.amount > 0)
        .map(fee => `${fee.description}: $${parseFloat(fee.amount).toFixed(2)}`);
      
      return feeItems.length > 0 ? feeItems.join(', ') : 'No fees';
    }
    
    // Handle legacy object-based fee structure for backward compatibility
    const feeItems = [];
    if (fees.entryFee) feeItems.push(`Entry: $${fees.entryFee}`);
    if (fees.lateFee) feeItems.push(`Late: $${fees.lateFee}`);
    if (fees.stallFee) feeItems.push(`Stall: $${fees.stallFee}`);
    if (fees.campFee) feeItems.push(`Camp: $${fees.campFee}`);
    if (fees.officeFee) feeItems.push(`Office: $${fees.officeFee}`);
    if (fees.otherFees) feeItems.push(`Other: $${fees.otherFees}`);
    
    return feeItems.length > 0 ? feeItems.join(', ') : `Total: $${fees.total || 0}`;
  };

  const getEventCount = (entry) => {
    return entry.entry_data?.events?.length || 0;
  };

  const calculateTotalAmount = (entry) => {
    let total = 0;
    
    // Add event amounts
    if (entry.entry_data?.events && Array.isArray(entry.entry_data.events)) {
      total += entry.entry_data.events.reduce((sum, event) => {
        const base = parseFloat(event.EntryAmount) || 0;
        const carry = Array.isArray(event.carryOvers)
          ? event.carryOvers.reduce((cSum, co) => {
              const v = parseFloat(co?.CoAmount || 0);
              return cSum + (isNaN(v) ? 0 : v);
            }, 0)
          : 0;
        return sum + base + carry;
      }, 0);
    }
    
    // Add fee amounts (quantity * amount)
    if (entry.entry_data?.fees && Array.isArray(entry.entry_data.fees)) {
      total += entry.entry_data.fees.reduce((sum, fee) => {
        const quantity = parseInt(fee.quantity) || 1;
        const amount = parseFloat(fee.amount) || 0;
        return sum + (quantity * amount);
      }, 0);
    }
    
    return total;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Disclaimer */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 sm:p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-xs sm:text-sm text-yellow-800">
                <strong>Disclaimer:</strong> 
                <span className="hidden sm:inline"> Imported Entries information is provided by the originating association. Accuracy and completeness of this data are the sole responsibility of the association.</span>
                <span className="sm:hidden"> Tap for details</span>
              </p>
            </div>
          </div>
          <button 
            className="sm:hidden p-1 text-yellow-600 hover:text-yellow-800 transition-colors"
            onClick={() => setShowDisclaimerDetails(!showDisclaimerDetails)}
            aria-label="Toggle disclaimer details"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        {/* Mobile expandable details */}
        <div className={`sm:hidden mt-2 pt-2 border-t border-yellow-200 ${showDisclaimerDetails ? 'block' : 'hidden'}`}>
          <p className="text-xs text-yellow-800">
            Imported Entries information is provided by the originating association. Accuracy and completeness of this data are the sole responsibility of the association.
          </p>
        </div>
      </div>

      {/* Main content with responsive design */}
      <div className="overflow-x-auto">
        <div className="min-w-0">
          {/* Header */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-8 h-8 text-black-500" />
                Entries
              </h1>
              <p className="text-gray-600">Manage your entries</p>
            </div>

            
            {/* Desktop: Full buttons with text */}
            <div className="hidden sm:flex flex-col lg:flex-row gap-2 w-full lg:w-auto justify-center">
              {familyAssociations.length > 0 && (
                <button
                onClick={() => {
                  // Initialize with primary association as default
                  const primary = primaryAccountSettings;
                  if (primary.associationKey) {
                    setSqlServerData(prev => ({
                      ...prev,
                      associationKey: primary.associationKey,
                      memberKey: primary.userId,
                      memberPass: primary.userPassword
                    }));
                    setAssociationName(primary.associationName || associationName);
                  }
                  setSelectedOnlineEntryAssociationId('primary');
                  setShowAssociationSelectionModal(true);
                }}
                disabled={showsLoading}
                className="flex items-center justify-center px-3 py-2 bg-[#6b4f2e] text-white rounded-lg hover:bg-[#7a5a35] disabled:bg-[#6b4f2e]/60 transition-colors text-sm"
                title="Add New Online Entry"
              >
                  {showsLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Globe className="w-4 h-4 mr-2" />
                  )}
                  <span>Add New Online Entry</span>
                </button>
              )}
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="flex items-center justify-center px-2 py-2 sm:px-4 sm:py-2 bg-[#51380d] text-white rounded-lg hover:bg-[#3d2a0a] transition-colors text-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                <span>Add New Entry</span>
              </button>
              <button
                onClick={exportEntries}
                className="flex items-center justify-center px-2 py-2 sm:px-4 sm:py-2 bg-[#b97a3a] text-white rounded-lg hover:bg-[#c98540] transition-colors text-sm"
                title="Export CSV"
              >
                <Download className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Export CSV</span>
              </button>
            </div>
          </div>

          {/* Mobile: Icons only in horizontal row - centered */}
          <div className="sm:hidden flex justify-center w-full mt-4">
            <div className="flex space-x-3">
              {familyAssociations.length > 0 && (
                <button
                  onClick={() => {
                    // Initialize with primary association as default (mobile)
                    const primary = primaryAccountSettings;
                    if (primary.associationKey) {
                      setSqlServerData(prev => ({
                        ...prev,
                        associationKey: primary.associationKey,
                        memberKey: primary.userId,
                        memberPass: primary.userPassword
                      }));
                      setAssociationName(primary.associationName || associationName);
                    }
                    setSelectedOnlineEntryAssociationId('primary');
                    setShowAssociationSelectionModal(true);
                  }}
                  disabled={showsLoading}
                  className="flex items-center justify-center p-3 bg-[#6b4f2e] text-white rounded-lg hover:bg-[#7a5a35] disabled:bg-[#6b4f2e]/60 transition-colors"
                  title="Add New Online Entry"
                >
                  {showsLoading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Globe className="w-5 h-5" />
                  )}
                </button>
              )}
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="flex items-center justify-center p-3 bg-[#51380d] text-white rounded-lg hover:bg-[#3d2a0a] transition-colors"
                title="Add New Entry"
              >
                <Plus className="w-5 h-5" />
              </button>
              <button
                onClick={exportEntries}
                className="flex items-center justify-center p-3 bg-[#b97a3a] text-white rounded-lg hover:bg-[#c98540] transition-colors"
                title="Export CSV"
              >
                <Download className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow mt-6">
            {/* Mobile: Expandable filters */}
            <div className="sm:hidden">
              <button
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                className="flex items-center justify-between w-full p-3 text-left bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <span className="font-medium text-gray-700">Filters</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">
                    {filteredEntries.length} of {entries.length} entries
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${filtersExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              {filtersExpanded && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search entries..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                    <select
                      value={filterSourceType}
                      onChange={(e) => setFilterSourceType(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Sources</option>
                      <option value="manual">Manual</option>
                      <option value="kcirodeo">Imported</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="created_at">Date Created</option>
                      <option value="show_start_date">Show Date</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            
            {/* Desktop: Always visible filters */}
            <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search entries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <select
                  value={filterSourceType}
                  onChange={(e) => setFilterSourceType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Sources</option>
                  <option value="manual">Manual</option>
                  <option value="kcirodeo">Imported</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="created_at">Date Created</option>
                  <option value="show_start_date">Show Date</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <span className="text-sm text-gray-600">
                  {filteredEntries.length} of {entries.length} entries
                </span>
              </div>
            </div>
          </div>

          {/* Entries List */}
          <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
            {filteredEntries.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No entries found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {entries.length === 0 ? 'No entries have been received yet.' : 'Try adjusting your search or filters.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto max-w-full">
                <table className="w-full sm:divide-y sm:divide-gray-200 border-separate border-spacing-y-2 sm:border-spacing-y-0">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 sm:px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Show & Events
                      </th>
                      <th className="hidden md:table-cell px-3 sm:px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="hidden xl:table-cell px-3 sm:px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Confirmation
                      </th>
                      <th className="hidden xl:table-cell px-3 sm:px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date Entered
                      </th>
                      <th className="hidden lg:table-cell px-3 sm:px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Source
                      </th>
                      <th className="hidden xl:table-cell px-3 sm:px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Imported Date
                      </th>
                      <th className="hidden sm:table-cell px-3 sm:px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Actions
                       </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEntries.map((entry) => (
                      <tr 
                        key={entry.id} 
                        className="hover:bg-gray-50"
                      >
                        <td className="px-3 sm:px-4 lg:px-6 py-2 sm:py-4">
                          {/* Mobile layout: show name on top, date and contestant below; no event count */}
                          <div className="sm:hidden bg-white rounded-lg border border-gray-200 p-3">
                                                          <div className="text-sm text-gray-900">
                                <div className="font-medium truncate">{entry.show_name || 'N/A'}</div>
                                {/* Show ownership if different from current user */}
                                {entry.owner_name && (
                                  <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded mt-1 inline-block">
                                    {entry.owner_name}
                                  </div>
                                )}
                                <div className="text-gray-500 mt-0.5 sm:mt-1">
                                  {entry.show_start_date && (
                                    <div className="flex items-center">
                                      <Calendar className="w-3 h-3 mr-1" />
                                      {format(parseISO(entry.show_start_date), 'MM/dd/yy')}
                                      {entry.confirmation_number && (
                                        <span className="ml-2">{entry.confirmation_number}</span>
                                      )}
                                    </div>
                                  )}
                                  {entry.entry_data?.dateentered && (
                                    <div className="text-xs text-gray-600 mt-1">
                                      Entered: {format(parseISO(entry.entry_data.dateentered), 'MM/dd/yy')}
                                    </div>
                                  )}
                                  <div className="text-xs mt-1">
                                    {entry.entry_data?.events?.[0]?.contestants?.[0]?.FirstName && 
                                     entry.entry_data?.events?.[0]?.contestants?.[0]?.LastName ? 
                                     `${entry.entry_data.events[0].contestants[0].FirstName} ${entry.entry_data.events[0].contestants[0].LastName}` : 
                                     'N/A'}
                                  </div>
                                </div>
                                                        {/* Mobile actions - icon only */}
                          <div className="mt-2 flex justify-center space-x-2">
                            <button
                              onClick={() => viewEntryDetails(entry)}
                              className="p-2 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200"
                              aria-label="View Details"
                              title="View Details"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            {entry.entry_data?.source !== 'kcirodeo' && (
                              <button
                                onClick={() => editEntry(entry)}
                                className="p-2 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200"
                                aria-label="Edit"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => createCalendarEventFromEntry(entry)}
                              className="p-2 rounded text-green-600 hover:text-green-800 hover:bg-green-50 border border-green-200"
                              aria-label="Add to Calendar"
                              title="Add to Calendar"
                            >
                              <CalendarDays className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => confirmDelete(entry)}
                              className="p-2 rounded text-red-600 hover:text-red-800 hover:bg-red-50 border border-red-200"
                              aria-label="Delete"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                            </div>
                          </div>

                          {/* Desktop/Tablet layout: original with event count */}
                          <div className="hidden sm:block">
                            <div className="text-sm text-gray-900">
                              <div className="font-medium flex items-center">
                                <Award className="w-4 h-4 mr-1 text-yellow-500" />
                                <span className="truncate">{entry.show_name || 'N/A'}</span>
                              </div>
                              {/* Show ownership if different from current user */}
                              {entry.owner_name && (
                                <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded mt-1 inline-block">
                                  {entry.owner_name}
                                </div>
                              )}
                              <div className="text-gray-500 mt-1">
                                {entry.show_start_date && (
                                  <div className="flex items-center">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    {format(parseISO(entry.show_start_date), 'MMM dd, yyyy')}
                                  </div>
                                )}
                                {getEventCount(entry) > 0 && (
                                  <div className="text-xs text-blue-600 mt-1">
                                    {getEventCount(entry)} event{getEventCount(entry) !== 1 ? 's' : ''}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        <td className="hidden md:table-cell px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="font-medium">
                              ${calculateTotalAmount(entry).toFixed(2)}
                            </div>
                          </div>
                        </td>
                        
                        <td className="hidden xl:table-cell px-3 sm:px-4 lg:px-6 py-2 sm:py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <span className="truncate block">{entry.confirmation_number || 'N/A'}</span>
                          </div>
                        </td>
                        
                        <td className="hidden xl:table-cell px-3 sm:px-4 lg:px-6 py-2 sm:py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <span className="truncate block">
                              {entry.entry_data?.dateentered ? 
                                format(parseISO(entry.entry_data.dateentered), 'MMM dd, yyyy') : 
                                'N/A'
                              }
                            </span>
                          </div>
                        </td>
                        
                        <td className="hidden lg:table-cell px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            entry.entry_data?.source === 'kcirodeo' 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {entry.entry_data?.source === 'kcirodeo' ? 'Imported' : 'Manual'}
                          </span>
                        </td>
                        
                        <td className="hidden xl:table-cell px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(parseISO(entry.created_at), 'MMM dd, yyyy')}
                        </td>

                                                 <td className="hidden sm:table-cell px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2" onClick={(e) => e.stopPropagation()}>
                            {/* View button for all entries */}
                            <button
                              onClick={() => viewEntryDetails(entry)}
                              className="px-2 py-1 rounded text-xs text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-200"
                              title="View Details"
                            >
                              View
                            </button>
                            
                            {/* Edit button only for manual entries */}
                            {entry.entry_data?.source !== 'kcirodeo' && (
                              <button
                                onClick={() => editEntry(entry)}
                                className="px-2 py-1 rounded text-xs text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-200"
                                title="Edit"
                              >
                                Edit
                              </button>
                            )}
                            
                            {/* Add to Calendar button */}
                            <button
                              onClick={() => createCalendarEventFromEntry(entry)}
                              className="px-2 py-1 rounded text-xs text-green-600 hover:text-green-900 hover:bg-green-50 border border-green-200"
                              title="Add to Calendar"
                            >
                              Calendar
                            </button>
                            
                            {/* Delete button */}
                            <button
                              onClick={() => confirmDelete(entry)}
                              className="px-2 py-1 rounded text-xs text-red-600 hover:text-red-900 hover:bg-red-50 border border-red-200"
                              title="Delete"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Entry Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Add New Entry</h2>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Entry Details - Confirmation Number and Date Entered */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Entry Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Confirmation Number</label>
                    <input
                      type="text"
                      value={newEntry.confirmationNumber}
                      onChange={(e) => setNewEntry(prev => ({
                        ...prev,
                        confirmationNumber: e.target.value
                      }))}
                      className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter confirmation number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Date Entered</label>
                    <input
                      type="date"
                      value={newEntry.dateentered || ''}
                      onChange={(e) => setNewEntry(prev => ({
                        ...prev,
                        dateentered: e.target.value
                      }))}
                      className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Family Member Selection */}
              {hasFeature('family_plan') && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Family Member
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Assign to Family Member</label>
                    <select
                      value={newEntry.family_member_id || 'primary'}
                      onChange={(e) => {
                        setNewEntry(prev => ({
                          ...prev,
                          family_member_id: e.target.value
                        }));
                        setSelectedFamilyMemberForEntry(e.target.value);
                      }}
                      className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {familyMembers.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Show Information */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Award className="w-5 h-5 mr-2" />
                  Show Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Show Name</label>
                    <input
                      type="text"
                      value={newEntry.show.showname}
                      onChange={(e) => setNewEntry(prev => ({
                        ...prev,
                        show: { ...prev.show, showname: e.target.value }
                      }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      <input
                        type="text"
                        value={newEntry.show.showlocation}
                        onChange={(e) => setNewEntry(prev => ({
                          ...prev,
                          show: { ...prev.show, showlocation: e.target.value }
                        }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Show Date</label>
                      <input
                        type="date"
                        value={newEntry.show.showdate}
                        onChange={(e) => setNewEntry(prev => ({
                          ...prev,
                          show: { ...prev.show, showdate: e.target.value }
                        }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Events */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Events
                  </h3>
                  <button
                    onClick={addEvent}
                    className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Event
                  </button>
                </div>
                {newEntry.events.map((event, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 bg-white">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium">Event {index + 1}</h4>
                      {newEntry.events.length > 1 && (
                        <button
                          onClick={() => removeEvent(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Event Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
                        <input
                          type="text"
                          value={event.eventname}
                          onChange={(e) => updateEvent(index, 'eventname', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Performance</label>
                        <input
                          type="text"
                          value={event.perfname}
                          onChange={(e) => updateEvent(index, 'perfname', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Entry Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={event.EntryAmount}
                          onChange={(e) => updateEvent(index, 'EntryAmount', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Contestants for this Event */}
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="text-sm font-medium text-gray-700">Contestants</h5>
                        <button
                          onClick={() => addContestantToEvent(index)}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Contestant
                        </button>
                      </div>
                      
                      {event.contestants.map((contestant, contestantIndex) => (
                        <div key={contestantIndex} className="border border-gray-200 rounded-lg p-3 mb-3 bg-gray-50">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-medium text-gray-600">Contestant {contestantIndex + 1}</span>
                            {event.contestants.length > 1 && (
                              <button
                                onClick={() => removeContestantFromEvent(index, contestantIndex)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
                              <input
                                type="text"
                                value={contestant.FirstName || ''}
                                onChange={(e) => updateEventContestant(index, contestantIndex, 'FirstName', e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="First Name"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
                              <input
                                type="text"
                                value={contestant.LastName || ''}
                                onChange={(e) => updateEventContestant(index, contestantIndex, 'LastName', e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Last Name"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Stock Name</label>
                              <input
                                type="text"
                                value={contestant.stockName || ''}
                                onChange={(e) => updateEventContestant(index, contestantIndex, 'stockName', e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Stock Name"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Position Description</label>
                              <input
                                type="text"
                                value={contestant.positiondesc || ''}
                                onChange={(e) => updateEventContestant(index, contestantIndex, 'positiondesc', e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Position"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Other Information */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Other Information
                  </h3>
                  <button
                    onClick={() => setNewEntry(prev => ({
                      ...prev,
                      other: [...prev.other, { othername: '', othervalue: '' }]
                    }))}
                    className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Other Field
                  </button>
                </div>
                {newEntry.other.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 bg-white">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium">Other Field {index + 1}</h4>
                      <button
                        onClick={() => setNewEntry(prev => ({
                          ...prev,
                          other: prev.other.filter((_, i) => i !== index)
                        }))}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                        <input
                          type="text"
                          value={item.othername}
                          onChange={(e) => setNewEntry(prev => ({
                            ...prev,
                            other: prev.other.map((otherItem, i) => 
                              i === index ? { ...otherItem, othername: e.target.value } : otherItem
                            )
                          }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Draw Number, Class"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                        <input
                          type="text"
                          value={item.othervalue}
                          onChange={(e) => setNewEntry(prev => ({
                            ...prev,
                            other: prev.other.map((otherItem, i) => 
                              i === index ? { ...otherItem, othervalue: e.target.value } : otherItem
                            )
                          }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter value"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Fees */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2" />
                    Fees
                  </h3>
                  <button
                    onClick={addFee}
                    className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Fee
                  </button>
                </div>
                {newEntry.fees.map((fee, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 bg-white">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium">Fee {index + 1}</h4>
                      {newEntry.fees.length > 1 && (
                        <button
                          onClick={() => removeFee(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <input
                          type="text"
                          placeholder="e.g., Stall Fee, Late Fee"
                          value={fee.description}
                          onChange={(e) => updateFee(index, 'description', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                        <input
                          type="number"
                          min="1"
                          value={fee.quantity}
                          onChange={(e) => updateFee(index, 'quantity', parseInt(e.target.value) || 1)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={fee.amount}
                          onChange={(e) => updateFee(index, 'amount', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-blue-900">Total Fees:</span>
                    <span className="font-bold text-blue-900">
                      ${newEntry.fees.reduce((sum, fee) => {
                        const quantity = parseInt(fee.quantity) || 1;
                        const amount = parseFloat(fee.amount) || 0;
                        return sum + (quantity * amount);
                      }, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Grand Total Summary */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-xl font-bold mb-4 flex items-center text-blue-900">
                  <DollarSign className="w-6 h-6 mr-2" />
                  Grand Total Summary
                </h3>
                <div className="space-y-3">
                  {/* Events Subtotal */}
                  <div className="flex justify-between items-center text-lg">
                    <span className="font-medium text-blue-900">Events Subtotal:</span>
                    <span className="font-semibold text-blue-900">
                      ${newEntry.events.reduce((sum, event) => sum + (parseFloat(event.EntryAmount) || 0), 0).toFixed(2)}
                    </span>
                  </div>
                  {/* Fees Subtotal */}
                  <div className="flex justify-between items-center text-lg">
                    <span className="font-medium text-blue-900">Fees Subtotal:</span>
                    <span className="font-semibold text-blue-900">
                      ${newEntry.fees.reduce((sum, fee) => {
                        const quantity = parseInt(fee.quantity) || 1;
                        const amount = parseFloat(fee.amount) || 0;
                        return sum + (quantity * amount);
                      }, 0).toFixed(2)}
                    </span>
                  </div>
                  {/* Grand Total */}
                  <div className="border-t-2 border-blue-300 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-bold text-blue-900">Grand Total:</span>
                      <span className="text-2xl font-bold text-blue-900">
                        ${(
                          newEntry.events.reduce((sum, event) => sum + (parseFloat(event.EntryAmount) || 0), 0) +
                          newEntry.fees.reduce((sum, fee) => {
                          const quantity = parseInt(fee.quantity) || 1;
                          const amount = parseFloat(fee.amount) || 0;
                          return sum + (quantity * amount);
                        }, 0)
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={addNewEntry}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
                >
                  {loading ? 'Adding...' : 'Add Entry'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SQL Server Import Modal */}
      {showSqlServerForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg sm:text-xl font-bold">Import Entries</h2>
              <button
                onClick={resetSqlServerImport}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="space-y-6">
              
              {/* Family Member Selection (Associations for import) */}
              {hasFeature('family_plan') && familyAssociations.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {familyAssociations.length === 1 ? 'Import From' : 'Select an Association to Import From'}
                    </label>
                        <select
                          value={selectedFamilyMemberId}
                          onChange={(e) => handleFamilyMemberChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {familyAssociations.map(a => (
                            <option key={a.id} value={String(a.id)}>
                              {a.familyMemberName} - {a.associationName}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                        {familyAssociations.length === 1 
                          ? 'Using your association credentials for importing'
                          : 'Choose which family member\'s association credentials to use for importing'
                        }
                      </p>
                  </div>
                )}


              {/* Import Actions */}
              <div className="bg-gray-50 p-4 rounded-lg">

                {/* Confirmation number input removed per UX request */}
                <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
                  <button
                    onClick={fetchSqlServerEntries}
                    disabled={sqlServerLoading || !selectedKCIRShow}
                    className="flex items-center justify-center px-4 py-2 bg-[#8b6b3d] text-white rounded-lg hover:bg-[#a17a4a] transition-colors disabled:opacity-50 text-sm"
                  >
                    {sqlServerLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Fetching...
                      </>
                    ) : !selectedKCIRShow ? (
                      <>
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Select Show First
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Fetch
                      </>
                    )}
                  </button>
                  <button
                    onClick={resetSqlServerImport}
                    className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Found Entries */}
              {sqlServerEntries.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                    <h3 className="text-lg font-semibold">Found Entries ({sqlServerEntries.length} shows)</h3>
                    <button
                      onClick={() => saveSqlServerEntriesToDatabase()}
                      disabled={loading}
                      className="flex items-center justify-center px-3 py-2 sm:px-4 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm w-full sm:w-auto"
                    >
                      {loading ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Save
                        </>
                      )}
                    </button>
                  </div>
                  
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {sqlServerEntries.map((entry, index) => (
                      <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                              <h4 className="font-semibold text-base sm:text-lg truncate">{entry.show?.showname || 'Unknown Show'}</h4>
                              <span className="text-sm text-gray-600">
                                (Confirmation: {entry.confirmationNumber})
                              </span>
                            </div>
                            <p className="text-gray-600">
                              {entry.show?.showdate && format(parseISO(entry.show.showdate), 'MMM dd, yyyy')}
                            </p>
                            {entry.dateentered && (
                              <p className="text-sm text-gray-500">
                                Date Entered: {format(parseISO(entry.dateentered), 'MMM dd, yyyy')}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => deleteSqlServerShow(index)}
                            className="text-red-600 hover:text-red-800 ml-4 flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Show Selection Modal */}
      {showSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Select a Show</h2>
              <button
                onClick={() => setShowSelectionModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {showsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 mr-2 animate-spin" />
                  <span>Loading shows...</span>
                </div>
              ) : availableShows.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No shows available for this association.
                </div>
              ) : (
                <div className="space-y-3">
                  {availableShows.map((show, index) => (
                    <button
                      key={index}
                      onClick={() => handleShowSelection(show)}
                      className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <div className="font-medium text-gray-900">{show.showName}</div>
                      <div className="text-sm text-gray-600">
                        {show.showCity && show.showState && (
                          <span className="mr-4">{show.showCity}, {show.showState}</span>
                        )}
                        {show.showDate && (
                          <span>{show.showDate}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* KCIRodeo Entry Form Iframe Modal */}
      {showIframe && selectedShow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-7xl h-[90vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <h2 className="text-lg sm:text-xl font-bold">
                  Enter {selectedShow.showName} - {associationName}
                </h2>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={openInNewTab}
                  className="text-green-600 hover:text-green-800 transition-colors p-2"
                  title="Open in new tab (recommended for entry issues)"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
                <button
                  onClick={refreshIframe}
                  className="text-blue-600 hover:text-blue-800 transition-colors p-2"
                  title="Refresh page"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
                <button
                  onClick={closeIframe}
                  className="text-gray-500 hover:text-gray-700"
                >
                                          <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>
            
            {/* Session State Information */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-2 sm:p-3 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-2 sm:ml-3">
                    <p className="text-xs sm:text-sm text-blue-700">
                      <strong>Note:</strong> 
                      <span className="hidden sm:inline"> If you experience issues within the entry form, use the "Open in new tab" button (green icon).</span>
                      <span className="sm:hidden"> Tap for details</span>
                    </p>
                  </div>
                </div>
                <button 
                  className="sm:hidden p-1 text-blue-600 hover:text-blue-800 transition-colors"
                  onClick={() => setShowSessionNoteDetails(!showSessionNoteDetails)}
                  aria-label="Toggle session note details"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              {/* Mobile expandable details */}
              <div className={`sm:hidden mt-2 pt-2 border-t border-blue-200 ${showSessionNoteDetails ? 'block' : 'hidden'}`}>
                <p className="text-xs text-blue-700">
                  If you experience session state issues when navigating within the form, use the "Open in new tab" button (green icon) for better session management.
                </p>
              </div>
            </div>
            
            <div className="flex-1 border border-gray-300 rounded-lg overflow-hidden">
              {/* Session Warning */}
              {showSessionWarning && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Session Warning:</strong> Multiple page navigations detected. Your session state may be affected. 
                        Consider refreshing the form or starting over.
                      </p>
                      <div className="mt-2 flex space-x-2">
                        <button
                          onClick={refreshIframe}
                          className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700 transition-colors"
                        >
                          Refresh Form
                        </button>
                        <button
                          onClick={() => setShowSessionWarning(false)}
                          className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <iframe
                src={iframeUrl || `https://entry.kcirodeo.net/entry/std/Entryform.aspx?Association=${encodeURIComponent(associationName)}&Show=${encodeURIComponent(selectedShow.showKey)}`}
                className="w-full h-full"
                title={`Online Entry Form - ${selectedShow.showName}`}
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation allow-top-navigation-by-user-activation"
                allow="fullscreen; encrypted-media; payment"
                referrerPolicy="origin"
                onLoad={handleIframeLoad}
                onError={handleIframeError}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Entry Modal */}
      {showEditForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg sm:text-xl font-bold">Edit Entry</h2>
              <button
                onClick={cancelEdit}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Entry Details - Confirmation Number and Date Entered */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Entry Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Confirmation Number</label>
                    <input
                      type="text"
                      value={newEntry.confirmationNumber}
                      onChange={(e) => setNewEntry(prev => ({
                        ...prev,
                        confirmationNumber: e.target.value
                      }))}
                      className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter confirmation number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Date Entered</label>
                    <input
                      type="date"
                      value={newEntry.dateentered || ''}
                      onChange={(e) => setNewEntry(prev => ({
                        ...prev,
                        dateentered: e.target.value
                      }))}
                      className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Family Member Selection */}
              {hasFeature('family_plan') && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Family Member
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Assign to Family Member</label>
                    <select
                      value={newEntry.family_member_id || 'primary'}
                      onChange={(e) => {
                        setNewEntry(prev => ({
                          ...prev,
                          family_member_id: e.target.value
                        }));
                        setSelectedFamilyMemberForEntry(e.target.value);
                      }}
                      className="w-full border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {familyMembers.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Show Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Show Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Show Name</label>
                    <input
                      type="text"
                      value={newEntry.show.showname}
                                               onChange={(e) => {
                           setNewEntry(prev => ({
                             ...prev,
                             show: { ...prev.show, showname: e.target.value }
                           }));
                         }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      <input
                        type="text"
                        value={newEntry.show.showlocation}
                        onChange={(e) => setNewEntry(prev => ({
                          ...prev,
                          show: { ...prev.show, showlocation: e.target.value }
                        }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Show Date</label>
                      <input
                        type="date"
                        value={newEntry.show.showdate}
                        onChange={(e) => setNewEntry(prev => ({
                          ...prev,
                          show: { ...prev.show, showdate: e.target.value }
                        }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Events */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Events
                  </h3>
                  <button
                    onClick={addEvent}
                    className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Event
                  </button>
                </div>
                {newEntry.events.map((event, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 bg-white">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium">Event {index + 1}</h4>
                      {newEntry.events.length > 1 && (
                        <button
                          onClick={() => removeEvent(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Event Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
                        <input
                          type="text"
                          value={event.eventname}
                          onChange={(e) => updateEvent(index, 'eventname', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Performance</label>
                        <input
                          type="text"
                          value={event.perfname}
                          onChange={(e) => updateEvent(index, 'perfname', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Entry Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={event.EntryAmount}
                          onChange={(e) => updateEvent(index, 'EntryAmount', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Contestants for this Event */}
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="text-sm font-medium text-gray-700">Contestants</h5>
                        <button
                          onClick={() => addContestantToEvent(index)}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Contestant
                        </button>
                      </div>
                      
                      {event.contestants.map((contestant, contestantIndex) => (
                        <div key={contestantIndex} className="border border-gray-200 rounded-lg p-3 mb-3 bg-gray-50">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-medium text-gray-600">Contestant {contestantIndex + 1}</span>
                            {event.contestants.length > 1 && (
                              <button
                                onClick={() => removeContestantFromEvent(index, contestantIndex)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
                              <input
                                type="text"
                                value={contestant.FirstName || ''}
                                onChange={(e) => updateEventContestant(index, contestantIndex, 'FirstName', e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="First Name"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
                              <input
                                type="text"
                                value={contestant.LastName || ''}
                                onChange={(e) => updateEventContestant(index, contestantIndex, 'LastName', e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Last Name"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Stock Name</label>
                              <input
                                type="text"
                                value={contestant.stockName || ''}
                                onChange={(e) => updateEventContestant(index, contestantIndex, 'stockName', e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Stock Name"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Position Description</label>
                              <input
                                type="text"
                                value={contestant.positiondesc || ''}
                                onChange={(e) => updateEventContestant(index, contestantIndex, 'positiondesc', e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Position"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Other Information */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Other Information
                  </h3>
                  <button
                    onClick={() => setNewEntry(prev => ({
                      ...prev,
                      other: [...prev.other, { othername: '', othervalue: '' }]
                    }))}
                    className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Other Field
                  </button>
                </div>
                {newEntry.other.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 bg-white">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium">Other Field {index + 1}</h4>
                      <button
                        onClick={() => setNewEntry(prev => ({
                          ...prev,
                          other: prev.other.filter((_, i) => i !== index)
                        }))}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                        <input
                          type="text"
                          value={item.othername}
                          onChange={(e) => setNewEntry(prev => ({
                            ...prev,
                            other: prev.other.map((otherItem, i) => 
                              i === index ? { ...otherItem, othername: e.target.value } : otherItem
                            )
                          }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Notes, Stalls, RV Hookup"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                        <input
                          type="text"
                          value={item.othervalue}
                          onChange={(e) => setNewEntry(prev => ({
                            ...prev,
                            other: prev.other.map((otherItem, i) => 
                              i === index ? { ...otherItem, othervalue: e.target.value } : otherItem
                            )
                          }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter value"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Fees */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Fees</h3>
                  <button
                    onClick={addFee}
                    className="flex items-center px-3 py-1 bg-[#51380d] text-white rounded-lg hover:bg-[#3d2a0a] transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Fee
                  </button>
                </div>
                {newEntry.fees.map((fee, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium">Fee {index + 1}</h4>
                      {newEntry.fees.length > 1 && (
                        <button
                          onClick={() => removeFee(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <input
                          type="text"
                          placeholder="e.g., Stall Fee, Late Fee"
                          value={fee.description}
                          onChange={(e) => updateFee(index, 'description', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={fee.amount}
                          onChange={(e) => updateFee(index, 'amount', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-blue-900">Total Fees:</span>
                    <span className="font-bold text-blue-900">
                      ${newEntry.fees.reduce((sum, fee) => {
                        const quantity = parseInt(fee.quantity) || 1;
                        const amount = parseFloat(fee.amount) || 0;
                        return sum + (quantity * amount);
                      }, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  onClick={cancelEdit}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEditedEntry}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold">Confirm Delete</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this entry? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingEntry(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deletingEntry) {
                    deleteEntry(deletingEntry.id);
                  }
                  setShowDeleteConfirm(false);
                  setDeletingEntry(null);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Entry Details Modal */}
      {showDetailsModal && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg sm:text-xl font-bold">Entry Details</h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedEntry(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Confirmation Number and Date Entered - Moved to top */}
              <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                <h3 className="text-lg font-semibold mb-4 flex items-center text-blue-900">
                  <FileText className="w-5 h-5 mr-2" />
                  Entry Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Confirmation Number</label>
                    <p className="text-xl font-bold text-blue-900">
                      {selectedEntry.confirmation_number || selectedEntry.entry_data?.confirmationNumber || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Date Entered</label>
                    <p className="text-xl font-bold text-blue-900">
                      {selectedEntry.entry_data?.dateentered ? 
                        format(parseISO(selectedEntry.entry_data.dateentered), 'MMM dd, yyyy') : 
                        'N/A'
                      }
                    </p>
                  </div>
                </div>
              </div>



              {/* Show Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Award className="w-5 h-5 mr-2" />
                  Show Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Show Name</label>
                    <p className="text-gray-900">
                      {selectedEntry.show_name || selectedEntry.entry_data?.show?.showname || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <p className="text-gray-900">
                      {selectedEntry.show_location || selectedEntry.entry_data?.show?.showlocation || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <p className="text-gray-900">
                      {selectedEntry.show_start_date && format(parseISO(selectedEntry.show_start_date), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Events */}
              {selectedEntry.entry_data?.events && selectedEntry.entry_data.events.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Events ({selectedEntry.entry_data.events.length})
                  </h3>
                  <div className="space-y-4">
                    {selectedEntry.entry_data.events.map((event, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
                        {/* Event Header */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
                            <p className="text-gray-900 font-semibold">{event.eventname || event.name || 'N/A'}</p>
                          </div>
                          {(event.perfname || event.performanceName) && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Performance</label>
                              <p className="text-gray-900">{event.perfname || event.performanceName}</p>
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Entry Amount</label>
                            <p className="text-gray-900 font-semibold text-blue-900">
                              ${(parseFloat(event.EntryAmount || event.amount || 0)).toFixed(2)}
                            </p>
                          </div>
                        </div>

                                                  {/* Event Details */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                            {/* Contestant Information */}
                            {event.contestants && event.contestants.length > 0 && (
                              <div className="col-span-full mb-4">
                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Contestants</h4>
                                <div className="space-y-3">
                                  {event.contestants.map((contestant, contestantIndex) => (
                                    <div key={contestantIndex} className="border rounded-lg p-3 bg-gray-50">
                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {(contestant.FirstName || contestant.LastName) && (
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Contestant Name</label>
                                            <p className="text-sm text-gray-900">
                                              {contestant.FirstName && contestant.LastName ? `${contestant.FirstName} ${contestant.LastName}` : contestant.FirstName || contestant.LastName || 'N/A'}
                                            </p>
                                          </div>
                                        )}
                                        {contestant.stockName && (
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Stock Name</label>
                                            <p className="text-sm text-gray-900">{contestant.stockName}</p>
                                          </div>
                                        )}

                                        {contestant.positiondesc && (
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Position</label>
                                            <p className="text-sm text-gray-900">{contestant.positiondesc}</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Event-level fields (if they exist) */}
                            {event.stockName && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Stock Name</label>
                                <p className="text-sm text-gray-900">{event.stockName}</p>
                              </div>
                            )}
                            {event.perfname && (
                              <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Performance</label>
                              <p className="text-sm text-gray-900">{event.perfname}</p>
                            </div>
                          )}
                          {event.positiondesc && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Position Description</label>
                              <p className="text-sm text-gray-900">{event.positiondesc}</p>
                            </div>
                          )}
                          {event.amount && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                              <p className="text-sm text-gray-900">
                                <FormattedCurrency value={event.amount} />
                              </p>
                            </div>
                          )}
                          {event.fee && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Fee</label>
                              <p className="text-sm text-gray-900">
                                <FormattedCurrency value={event.fee} />
                              </p>
                            </div>
                          )}
                          {event.total && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Total</label>
                              <p className="text-sm text-gray-900">
                                <FormattedCurrency value={event.total} />
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Carry Overs */}
                        {event.carryOvers && event.carryOvers.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Carry Overs:</h4>
                            <div className="space-y-2">
                              {event.carryOvers.map((carryOver, coIndex) => (
                                <div key={coIndex} className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-300">
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <span className="text-sm font-medium text-gray-700">
                                        {carryOver.CoEventName || `Carry Over ${coIndex + 1}`}:
                                      </span>
                                      <span className="text-sm text-gray-900 ml-2">
                                        ${(parseFloat(carryOver.CoAmount || carryOver.coAmount || carryOver.EntryAmount || carryOver.entryAmount || carryOver.Amount || carryOver.amount || 0)).toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

                             {/* Other Data */}
               {selectedEntry.entry_data?.other && selectedEntry.entry_data.other.length > 0 && (
                 <div className="bg-gray-50 p-4 rounded-lg">
                   <h3 className="text-lg font-semibold mb-4 flex items-center">
                     <FileText className="w-5 h-5 mr-2" />
                     Other Information ({selectedEntry.entry_data.other.length})
                   </h3>
                   <div className="space-y-3">
                     {selectedEntry.entry_data.other.map((item, index) => (
                       <div key={index} className="bg-white p-3 rounded border">
                         <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                           <span className="text-sm font-medium text-gray-700">
                             {item.othername || 'N/A'}:
                           </span>
                           <span className="text-sm text-gray-900">
                             {item.othervalue || 'N/A'}
                           </span>
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
               )}

              {/* Fees */}
              {selectedEntry.entry_data?.fees && selectedEntry.entry_data.fees.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2" />
                    Fees ({selectedEntry.entry_data.fees.length})
                  </h3>
                  <div className="space-y-3">
                    {selectedEntry.entry_data.fees.map((fee, index) => (
                      <div key={index} className="flex justify-between items-center bg-white p-3 rounded border">
                        <div>
                          <span className="font-medium text-gray-900">{fee.description || fee.FeeName || 'N/A'}</span>
                          {fee.quantity && fee.quantity > 1 && (
                            <span className="text-sm text-gray-500 ml-2">(Qty: {fee.quantity})</span>
                          )}
                        </div>
                        <span className="font-bold text-gray-900">${((parseInt(fee.quantity) || 1) * (parseFloat(fee.amount || fee.FeeAmt || 0))).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-lg text-gray-900">Total Fees:</span>
                        <span className="font-bold text-lg text-blue-900">
                          ${(selectedEntry.total_fee || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Grand Total */}
              <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                <h3 className="text-xl font-bold mb-4 flex items-center text-blue-900">
                  <DollarSign className="w-6 h-6 mr-2" />
                  Grand Total
                </h3>
                <div className="space-y-3">
                  {/* Events Subtotal */}
                  {selectedEntry.entry_data?.events && selectedEntry.entry_data.events.length > 0 && (
                    <div className="flex justify-between items-center text-lg">
                      <span className="font-medium text-blue-900">Events Subtotal:</span>
                      <span className="font-semibold text-blue-900">
                        ${selectedEntry.entry_data.events.reduce((sum, event) => {
                          const base = parseFloat(event.EntryAmount || event.amount || 0);
                          const carry = Array.isArray(event.carryOvers)
                            ? event.carryOvers.reduce((cSum, co) => {
                                const v = parseFloat((co && (co.CoAmount ?? co.coAmount ?? co.EntryAmount ?? co.entryAmount ?? co.Amount ?? co.amount)));
                                return cSum + (isNaN(v) ? 0 : v);
                              }, 0)
                            : 0;
                          return sum + base + carry;
                        }, 0).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {/* Fees Subtotal */}
                  {selectedEntry.entry_data?.fees && selectedEntry.entry_data.fees.length > 0 && (
                    <div className="flex justify-between items-center text-lg">
                      <span className="font-medium text-blue-900">Fees Subtotal:</span>
                      <span className="font-semibold text-blue-900">
                        ${selectedEntry.entry_data.fees.reduce((sum, fee) => {
                          const quantity = parseInt(fee.quantity) || 1;
                          const amount = parseFloat(fee.amount || fee.FeeAmt || 0) || 0;
                          return sum + (quantity * amount);
                        }, 0).toFixed(2)}
                      </span>
                    </div>
                  )}

                  {/* Grand Total */}
                  <div className="border-t-2 border-blue-300 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-bold text-blue-900">Grand Total:</span>
                      <span className="text-2xl font-bold text-blue-900">
                        ${(
                          (selectedEntry.entry_data?.events?.reduce((sum, event) => {
                            const base = parseFloat(event.EntryAmount || event.amount || 0);
                            const carry = Array.isArray(event.carryOvers)
                              ? event.carryOvers.reduce((cSum, co) => {
                                  const v = parseFloat((co && (co.CoAmount ?? co.coAmount ?? co.EntryAmount ?? co.entryAmount ?? co.Amount ?? co.amount)));
                                  return cSum + (isNaN(v) ? 0 : v);
                                }, 0)
                              : 0;
                            return sum + base + carry;
                          }, 0) || 0) +
                          (selectedEntry.entry_data?.fees?.reduce((sum, fee) => {
                            const quantity = parseInt(fee.quantity) || 1;
                            const amount = parseFloat(fee.amount || fee.FeeAmt || 0) || 0;
                            return sum + (quantity * amount);
                          }, 0) || 0)
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payments Section */}
              {selectedEntry.entry_data?.payments && selectedEntry.entry_data.payments.length > 0 && (
                <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                  <h3 className="text-xl font-bold mb-4 flex items-center text-green-900">
                    <DollarSign className="w-6 h-6 mr-2" />
                    Payments
                  </h3>
                  <div className="space-y-3">
                    {selectedEntry.entry_data.payments.map((payment, index) => (
                      <div key={index} className="bg-white p-3 rounded border border-green-200">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-sm font-medium text-gray-700">
                              {payment.description || payment.paymentType || `Payment ${index + 1}`}:
                            </span>
                            <span className="text-sm text-gray-900 ml-2">
                              ${(parseFloat(payment.paymentamount || payment.amount || payment.Amount || 0)).toFixed(2)}
                            </span>
                            {payment.paymentdate && (
                              <span className="text-xs text-gray-500 ml-2">
                                ({formatDate(payment.paymentdate)})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Payment Total */}
                    <div className="border-t border-green-300 pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold text-green-900">Total Payments:</span>
                        <span className="text-lg font-bold text-green-900">
                          ${selectedEntry.entry_data.payments.reduce((sum, payment) => {
                            return sum + (parseFloat(payment.paymentamount || payment.amount || payment.Amount || 0));
                          }, 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Balance Calculation */}
                    <div className="bg-green-100 p-3 rounded border border-green-300">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold text-green-900">Balance Due:</span>
                        <span className="text-lg font-bold text-green-900">
                          ${(
                            (selectedEntry.entry_data?.events?.reduce((sum, event) => {
                              const base = parseFloat(event.EntryAmount || event.amount || 0);
                              const carry = Array.isArray(event.carryOvers)
                                ? event.carryOvers.reduce((cSum, co) => {
                                    const v = parseFloat((co && (co.CoAmount ?? co.coAmount ?? co.EntryAmount ?? co.entryAmount ?? co.Amount ?? co.amount)));
                                    return cSum + (isNaN(v) ? 0 : v);
                                  }, 0)
                                : 0;
                              return sum + base + carry;
                            }, 0) || 0) +
                            (selectedEntry.entry_data?.fees?.reduce((sum, fee) => {
                              const quantity = parseInt(fee.quantity) || 1;
                              const amount = parseFloat(fee.amount || fee.FeeAmt || 0) || 0;
                              return sum + (quantity * amount);
                            }, 0) || 0) -
                            (selectedEntry.entry_data.payments.reduce((sum, payment) => {
                              return sum + (parseFloat(payment.paymentamount || payment.amount || payment.Amount || 0));
                            }, 0))
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Additional Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Imported Date</label>
                    <p className="text-gray-900">{format(parseISO(selectedEntry.created_at), 'MMM dd, yyyy HH:mm')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Association Selection Modal for Add New Online Entry */}
      {showAssociationSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Select Association</h2>
              <button
                onClick={() => setShowAssociationSelectionModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Choose an association to view available online entries
                </label>
                <select
                  value={selectedOnlineEntryAssociationId}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    setSelectedOnlineEntryAssociationId(selectedValue);
                    if (selectedValue === 'primary') {
                      const p = primaryAccountSettings;
                      setSqlServerData(prev => ({
                        ...prev,
                        associationKey: p.associationKey || '',
                        memberKey: p.userId || '',
                        memberPass: p.userPassword || ''
                      }));
                      setAssociationName(p.associationName || associationName);
                    } else {
                      const chosen = onlineAssociations.find(x => x.id === selectedValue);
                      if (chosen) {
                        setSqlServerData(prev => ({
                          ...prev,
                          associationKey: chosen.associationKey || '',
                          memberKey: chosen.userId || '',
                          memberPass: chosen.userPassword || ''
                        }));
                        setAssociationName(chosen.associationName || '');
                      }
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {onlineAssociations.map(opt => (
                    <option key={opt.id} value={opt.id}>
                      {opt.associationName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowAssociationSelectionModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!sqlServerData.associationKey) {
                      showNotification('Please select an association first', 'error');
                      return;
                    }
                    setShowAssociationSelectionModal(false);
                    fetchAvailableShows();
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 